import { useRouter } from 'next/router';
import i18n, { SettingsType } from 'utils/i18n';
import { useEffect, useState } from 'react';
import { useUserData } from 'utils/user';
import Button from './button';
import Modal from './modal';
import Ratings from './ratings';
import styled from 'styled-components';

type RateButtonProps = {
  moduleId: string;
};

export const RateButton: React.FC<RateButtonProps> = ({ moduleId }) => {
  const { locale } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;
  const { achievements, loggedIn, login } = useUserData();
  const [rating, setRating] = useState<{ rating: number; hasRated: boolean } | null>(null);
  const [comment, setComment] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const [showModal, setShowModal] = useState<'None' | 'Login' | 'Rate'>('None');
  const [loading, setLoading] = useState(false);

  const moduleCompleted = achievements.find((ach) => ach.type === 'ModuleComplete' && ach.contentId === moduleId);

  const getUserRating = async (moduleCompletedSid: string): Promise<void> => {
    const response = await fetch('/api/get-user-rating', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: moduleCompletedSid,
      }),
    });
    if (response.status === 200) {
      const ratingResponse = await response.json();
      setRating(ratingResponse as { rating: number; hasRated: boolean });
    }
    setShowModal('None');
  };

  const rateModule = async (moduleCompletedSid: string, rating: number, comment: string): Promise<void> => {
    setLoading(true);
    const response = await fetch('/api/rate-module', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: moduleCompletedSid,
        rating,
        ...(comment && { comment }),
      }),
    });

    if (response.status === 200) {
      const ratingResponse = await response.json();
      setRating(ratingResponse as { rating: number; hasRated: boolean });
    }

    setLoading(false);
    setShowModal('None');
    setShowThankYou(true);
  };

  useEffect(() => {
    if (moduleCompleted?.sid && !rating) {
      getUserRating(moduleCompleted.sid);
    } else if (moduleCompleted?.contentId && !rating) {
      getUserRating('');
    }
  }, [moduleCompleted?.sid, moduleCompleted?.contentId, rating]);

  const resetRating = () => {
    setRating(null);
    setShowModal('None');
  };

  const ratingNext = (val: number) => {
    if (!loggedIn) {
      setShowModal('Login');
    } else {
      setShowModal('Rate');
    }

    return setRating((state) => ({ ...state, rating: val }));
  };

  return (
    <>
      <Modal
        visible={showModal === 'Login'}
        headline={dictionary.ratingRateSignInToRate}
        onClose={() => setShowModal('None')}
      >
        <div className="buttons">
          <Button mode="primary" size="small" onClick={() => login()}>
            {dictionary.login}
          </Button>
          <Button size="small" onClick={() => setShowModal('None')}>
            {dictionary.cancel}
          </Button>
        </div>
      </Modal>
      <Modal visible={showModal === 'Rate'} headline={dictionary.ratingRateModalHeadline} onClose={resetRating}>
        <p>{dictionary.ratingRateModalBody}</p>
        <Ratings value={rating?.rating} onChange={(val) => setRating((state) => ({ ...state, rating: val }))} max={5} />
        <div className="value">
          <StyledTextAreaField
            placeholder={dictionary.rateTextFieldPlaceholder}
            onChange={(event) => setComment(event.target.value)}
            maxLength={500}
          />
        </div>
        <div className="buttons center">
          <Button
            mode="primary"
            size="small"
            loading={loading}
            disabled={!rating?.rating}
            onClick={() => rateModule(moduleCompleted?.sid, rating?.rating, comment)}
          >
            {dictionary.submit}
          </Button>
        </div>
      </Modal>
      {rating && !rating.hasRated && (
        <StyledRatingContainer>
          <p>{dictionary.helpUsImprove}</p>{' '}
          <Ratings className="ratings" value={rating?.rating} onChange={ratingNext} max={5} />
        </StyledRatingContainer>
      )}
      {showThankYou && (
        <StyledRatingContainer>
          <p>{dictionary.thankYouForReview}</p>
        </StyledRatingContainer>
      )}
    </>
  );
};

const StyledTextAreaField = styled.textarea`
  width: 100%;
  height: 120px;
  padding: calc(0.375em - 1px) calc(0.625em - 1px);
`;

const StyledRatingContainer = styled.div`
  margin-top: 2rem;

  .ratings {
    margin-top: 0;
  }
`;

export default RateButton;
